// src/services/receptionist/index.ts
//
// WhatsApp AI Receptionist console API client — ourteam-frontend#3606, BE epic
// ourteam-backend#4666. Contract and JSON shapes are in ./types.ts and mirror
// the BE tickets exactly: the payloads bind straight into the interfaces the
// console was built against, in camelCase, and a renamed field here is a
// silently broken screen.
//
// Base path: the console routes are mounted at `/api/v1/receptionist/...`,
// the same mount point the AEO routes use (see src/services/aeo/index.ts).
//
// Data source, two flags with a fixed precedence:
//
//   VITE_RECEPTIONIST_MOCK_DATA="true"      → the demo dataset in ./devMock, and
//                                             the backend is never called. The
//                                             explicit switch for demos and QA
//                                             while the console API is in review.
//   VITE_ENABLE_RECEPTIONIST_LIVE_API="true" → the real API, but ONLY when the
//                                             mock flag is not "true".
//   neither                                  → fixtures, because there is
//                                             nothing else to call.
//
// Mock beats live on purpose: an environment that has both set cannot reach a
// backend by accident, and turning the demo off is one deliberate change.
// Delete both flags and devMock.ts once the API is live everywhere.
//
// Shape, copied from the AEO client because it has held up:
//   • `if (useFixtures)` is the FIRST line of every function
//   • `res.data?.data ?? res.data` — tolerate the envelope and a bare body
//   • degrade where the screen can still render; throw only where the caller
//     genuinely must handle it
//   • an EXPECTED non-2xx (404 "not yours", 409 "already decided", 422 with the
//     validator's words) comes back as a result union, never as an exception,
//     because the screen has to tell it from a failure to ask

import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import * as devMock from "./devMock";
import { toMerchantInput, toFlowInput } from "./types";
import type {
    AnalyseResult,
    ApplyProposalsResult,
    BookingDecisionResult,
    BookingMessageInput,
    SendBookingMessageResult,
    StaffChannelState,
    SendStaffTestResult,
    BookingFlow,
    BookingsPage,
    FlowTemplate,
    ListBookingsParams,
    ListMerchantsParams,
    MerchantConfig,
    MerchantConfigInput,
    MerchantResult,
    MerchantsPage,
    MigrateResult,
    SaveFlowResult,
    SaveMerchantResult,
    ReceptionistOverview,
    ServiceImageResult,
    TranscriptFile,
    WhatsappState,
} from "./types";

export type {
    AnalyseResult,
    AnswerKnockoutGate,
    ApplyProposalsResult,
    Booking,
    BookingAdvance,
    BookingDecisionResult,
    BookingMessageInput,
    SendBookingMessageResult,
    BookingFlow,
    BookingFlowStep,
    BookingGate,
    BookingQuestion,
    BookingStatus,
    BookingStepType,
    BookingsFilter,
    BookingsPage,
    DeadlineGate,
    EscalationRule,
    FlowTemplate,
    FollowUp,
    FollowUpTrigger,
    KnockoutOperator,
    ListBookingsParams,
    ListMerchantsParams,
    MerchantCategory,
    MerchantConfig,
    MerchantConfigInput,
    MerchantResult,
    MerchantSummary,
    MerchantsPage,
    MigrateResult,
    OnboardingStage,
    Opener,
    OpeningHours,
    Proposal,
    ProposalKind,
    RequestFailure,
    SaveFlowResult,
    SaveMerchantResult,
    ReceptionistOverview,
    ServiceImageResult,
    Service,
    StaffMember,
    StopRule,
    TemplateStatus,
    Tone,
    SendStaffTestResult,
    StaffChannelMember,
    StaffChannelState,
    TranscriptFile,
    Turn,
    TurnAuthor,
    WhatsappChecklist,
    WhatsappState,
    WhatsappTemplate,
    WhatsappTemplateKey,
    WhatsappTemplateStatus,
} from "./types";
export { toMerchantInput, toFlowInput } from "./types";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
const RECEPTIONIST_BASE = `${apiURL}/api/v1/receptionist`;

const mockData = import.meta.env.VITE_RECEPTIONIST_MOCK_DATA === "true";
const liveApiEnabled = import.meta.env.VITE_ENABLE_RECEPTIONIST_LIVE_API === "true";
const useFixtures = mockData || !liveApiEnabled;

/** Exported for callers that must refuse to act on invented data. */
export const isReceptionistLiveApiEnabled = !useFixtures;
/** True while the screens run on the demo dataset. */
export const isReceptionistMockData = useFixtures;

// Say so once, loudly, outside dev — a silent fixture fallback on staging is
// exactly how "it looks like it works" turns into a wasted QA cycle. The
// wording says WHICH flag put us here, since there are two.
if (useFixtures && !import.meta.env.DEV) {
    console.warn(
        mockData
            ? "[receptionist] serving the DEMO dataset — VITE_RECEPTIONIST_MOCK_DATA is \"true\". Nothing on the /receptionist/* screens is real."
            : "[receptionist] serving FIXTURE data — VITE_ENABLE_RECEPTIONIST_LIVE_API is not \"true\". Nothing on the /receptionist/* screens is real. See ourteam-backend#4666.",
    );
}

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
}

const unwrap = <T>(res: { data?: { data?: T } & T }): T => (res.data?.data ?? res.data) as T;

/**
 * The reason a call failed, in words the person who pressed the button can
 * act on. Reads the platform's own `{ message, errors[] }` first, then
 * FastAPI's `detail` (a string, or a 422 array), then falls back to the status.
 */
function readApiError(err: unknown, fallback: string): { message: string; code?: string; status?: number; data?: Record<string, unknown> } {
    if (!axios.isAxiosError(err)) return { message: fallback };
    const res = err.response;
    if (!res) {
        const timedOut = err.code === "ECONNABORTED" || err.code === "ETIMEDOUT";
        return { message: timedOut ? "The server did not answer in time." : "Could not reach the server. Check your connection and try again." };
    }
    const data = (res.data ?? {}) as { message?: string; errors?: string[]; detail?: unknown; data?: Record<string, unknown> };
    if (data.message) return { message: data.message, code: data.errors?.[0], status: res.status, data: data.data };
    const { detail } = data;
    if (typeof detail === "string" && detail.trim()) return { message: detail, status: res.status };
    if (Array.isArray(detail)) {
        const parts = detail.map((d) => (d as { msg?: string })?.msg).filter((m): m is string => Boolean(m));
        if (parts.length) return { message: parts.join("; "), status: res.status };
    }
    return { message: res.status ? `${fallback} (HTTP ${res.status})` : fallback, status: res.status };
}

// ─── Merchant config (#4667) ─────────────────────────────────────────────────

/** GET .../merchants/{merchantId}. 404 is "not yours or not there", by design indistinguishable. */
export async function getMerchant(merchantId: string): Promise<MerchantResult> {
    if (useFixtures) return devMock.getMerchant(merchantId);
    try {
        const res = await axios.get(`${RECEPTIONIST_BASE}/merchants/${merchantId}`, { headers: authHeaders() });
        return { ok: true, merchant: unwrap<MerchantConfig>(res) };
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) return { ok: false, kind: "notFound" };
        catchErrorLog(`[receptionist] getMerchant failed for ${merchantId}: ${err}`);
        return { ok: false, kind: "error", message: readApiError(err, "Could not load this merchant").message };
    }
}

/**
 * PUT .../merchants/{merchantId}. Full replace; the server recomputes the locks
 * from `category`. A full config is accepted for convenience and stripped to
 * the writable fields HERE, so the read-only ones never leave the client —
 * that is the one place the rule has to hold rather than every screen.
 */
export async function saveMerchant(merchantId: string, config: MerchantConfig | MerchantConfigInput): Promise<SaveMerchantResult> {
    const body = "id" in config ? toMerchantInput(config) : config;
    if (useFixtures) return devMock.saveMerchant(merchantId, body);
    try {
        const res = await axios.put(`${RECEPTIONIST_BASE}/merchants/${merchantId}`, body, { headers: authHeaders() });
        return { ok: true, merchant: unwrap<MerchantConfig>(res) };
    } catch (err) {
        const e = readApiError(err, "Could not save");
        if (e.status === 422) return { ok: false, kind: "invalid", message: e.message, code: e.code };
        catchErrorLog(`[receptionist] saveMerchant failed for ${merchantId}: ${err}`);
        return { ok: false, kind: "error", message: e.message };
    }
}

/** JPEG, PNG or WebP up to 5 MB: the server's limits, checked here first so a wrong file says so at once. */
export const SERVICE_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const SERVICE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** POST .../services/{serviceId}/image (multipart, field `file`). The service must already be saved. */
export async function uploadServiceImage(merchantId: string, serviceId: string, file: File): Promise<ServiceImageResult> {
    if (useFixtures) return devMock.uploadServiceImage(merchantId, serviceId, file);
    try {
        const form = new FormData();
        form.append("file", file);
        // No Content-Type here: axios sets the multipart boundary itself.
        const headers = authHeaders();
        delete headers["Content-Type"];
        const res = await axios.post(`${RECEPTIONIST_BASE}/merchants/${merchantId}/services/${encodeURIComponent(serviceId)}/image`, form, { headers, timeout: 60000 });
        return { ok: true, merchant: unwrap<MerchantConfig>(res) };
    } catch (err) {
        const e = readApiError(err, "Could not upload the image");
        if (e.status !== 422 && e.status !== 404) catchErrorLog(`[receptionist] uploadServiceImage failed for ${merchantId}/${serviceId}: ${err}`);
        return { ok: false, message: e.message };
    }
}

/** DELETE .../services/{serviceId}/image. */
export async function deleteServiceImage(merchantId: string, serviceId: string): Promise<ServiceImageResult> {
    if (useFixtures) return devMock.deleteServiceImage(merchantId, serviceId);
    try {
        const res = await axios.delete(`${RECEPTIONIST_BASE}/merchants/${merchantId}/services/${encodeURIComponent(serviceId)}/image`, { headers: authHeaders() });
        return { ok: true, merchant: unwrap<MerchantConfig>(res) };
    } catch (err) {
        catchErrorLog(`[receptionist] deleteServiceImage failed for ${merchantId}/${serviceId}: ${err}`);
        return { ok: false, message: readApiError(err, "Could not remove the image").message };
    }
}

// ─── Overview (ourteam-backend#6718) ─────────────────────────────────────────

/** GET .../overview: what waits on the team, this week against last, today's appointments. Throws on failure. */
export async function getOverview(merchantId: string): Promise<ReceptionistOverview> {
    if (useFixtures) return devMock.getOverview(merchantId);
    try {
        const res = await axios.get(`${RECEPTIONIST_BASE}/merchants/${merchantId}/overview`, { headers: authHeaders() });
        return unwrap<ReceptionistOverview>(res);
    } catch (err) {
        catchErrorLog(`[receptionist] getOverview failed for ${merchantId}: ${err}`);
        throw err;
    }
}

// ─── Booking flow (#4668) ────────────────────────────────────────────────────

/** GET .../merchants/{merchantId}/flow. A merchant with no saved flow gets the default document, never a 404. */
export async function getFlow(merchantId: string): Promise<BookingFlow> {
    if (useFixtures) return devMock.getFlow(merchantId);
    try {
        const res = await axios.get(`${RECEPTIONIST_BASE}/merchants/${merchantId}/flow`, { headers: authHeaders() });
        const flow = unwrap<BookingFlow>(res);
        return { ...flow, steps: flow.steps ?? [], followUps: flow.followUps ?? [] };
    } catch (err) {
        catchErrorLog(`[receptionist] getFlow failed for ${merchantId}: ${err}`);
        throw err;
    }
}

/**
 * PUT .../merchants/{merchantId}/flow. A 422 carries `data.problems` in the
 * validator's own words. `templateStatus` is stripped from every follow-up
 * before the request leaves — it is the registry's, never the client's — so
 * the returned flow is the only place a status comes from.
 */
export async function saveFlow(merchantId: string, flow: BookingFlow): Promise<SaveFlowResult> {
    const body = toFlowInput(flow);
    if (useFixtures) return devMock.saveFlow(merchantId, body);
    try {
        const res = await axios.put(`${RECEPTIONIST_BASE}/merchants/${merchantId}/flow`, body, { headers: authHeaders() });
        return { ok: true, flow: unwrap<BookingFlow>(res) };
    } catch (err) {
        const e = readApiError(err, "Could not save the flow");
        if (e.status === 422) {
            const listed = e.data?.problems;
            const problems = Array.isArray(listed) ? (listed as string[]) : [e.message];
            return { ok: false, kind: "invalid", problems };
        }
        catchErrorLog(`[receptionist] saveFlow failed for ${merchantId}: ${err}`);
        return { ok: false, kind: "error", message: e.message };
    }
}

/** GET /flow-templates — exactly the two starter flows. Throws so the picker can hide itself. */
export async function getFlowTemplates(): Promise<FlowTemplate[]> {
    if (useFixtures) return devMock.getFlowTemplates();
    try {
        const res = await axios.get(`${RECEPTIONIST_BASE}/flow-templates`, { headers: authHeaders() });
        const body = unwrap<{ templates?: FlowTemplate[] }>(res);
        return Array.isArray(body?.templates) ? body.templates : [];
    } catch (err) {
        catchErrorLog(`[receptionist] getFlowTemplates failed: ${err}`);
        throw err;
    }
}

// ─── Bookings (#4669) ────────────────────────────────────────────────────────

/** GET .../merchants/{merchantId}/bookings?status=&page=&limit=. Counts cover the whole queue. */
export async function listBookings(merchantId: string, params: ListBookingsParams = {}): Promise<BookingsPage> {
    if (useFixtures) return devMock.listBookings(merchantId, params);
    try {
        const res = await axios.get(`${RECEPTIONIST_BASE}/merchants/${merchantId}/bookings`, { headers: authHeaders(), params });
        const body = unwrap<BookingsPage>(res);
        return { ...body, bookings: body.bookings ?? [] };
    } catch (err) {
        catchErrorLog(`[receptionist] listBookings failed for ${merchantId}: ${err}`);
        throw err;
    }
}

function decisionFailure(err: unknown, verb: string): BookingDecisionResult {
    const e = readApiError(err, `Could not ${verb} this booking`);
    if (e.status === 409 && e.code === "booking_not_pending") return { ok: false, kind: "not_pending", message: e.message };
    if (e.status === 409 && e.code === "template_not_approved") return { ok: false, kind: "template_not_approved", message: e.message };
    catchErrorLog(`[receptionist] ${verb} booking failed: ${err}`);
    return { ok: false, kind: "error", message: e.message };
}

/** POST .../bookings/{bookingId}/confirm — sets Confirmed AND messages the customer, the same message a staff `1` sends. */
export async function confirmBooking(bookingId: string, note?: string): Promise<BookingDecisionResult> {
    if (useFixtures) return devMock.confirmBooking(bookingId);
    try {
        const res = await axios.post(`${RECEPTIONIST_BASE}/bookings/${bookingId}/confirm`, note ? { note } : {}, { headers: authHeaders() });
        return { ok: true, booking: unwrap<BookingsPage["bookings"][number]>(res) };
    } catch (err) {
        return decisionFailure(err, "confirm");
    }
}

/** POST .../bookings/{bookingId}/decline. */
export async function declineBooking(bookingId: string, reason?: string): Promise<BookingDecisionResult> {
    if (useFixtures) return devMock.declineBooking(bookingId);
    try {
        const res = await axios.post(`${RECEPTIONIST_BASE}/bookings/${bookingId}/decline`, reason ? { reason } : {}, { headers: authHeaders() });
        return { ok: true, booking: unwrap<BookingsPage["bookings"][number]>(res) };
    } catch (err) {
        return decisionFailure(err, "decline");
    }
}

/**
 * POST .../bookings/{bookingId}/messages — a free-form reply on the thread.
 *
 * PROPOSED ENDPOINT: not in ourteam-backend#4669, which defines confirm and
 * decline only. Live calls will 404 until a BE ticket lands, so this is
 * exercised through the fixtures today. 409 is read as the 24-hour window
 * having closed, matching the candidate composer's contract (#3017).
 */
export async function sendBookingMessage(bookingId: string, input: BookingMessageInput): Promise<SendBookingMessageResult> {
    if (useFixtures) return devMock.sendBookingMessage(bookingId, input);
    try {
        const res = await axios.post(`${RECEPTIONIST_BASE}/bookings/${bookingId}/messages`, input, { headers: authHeaders() });
        return { ok: true, booking: unwrap<BookingsPage["bookings"][number]>(res) };
    } catch (err) {
        const { message, status } = readApiError(err, "Could not send the message");
        if (status === 409) {
            return {
                ok: false, kind: "window_closed",
                message: message || "WhatsApp only delivers replies within 24 hours of the customer's last message.",
            };
        }
        return { ok: false, kind: "error", message };
    }
}

// ─── Staff roll-up (#4670) ───────────────────────────────────────────────────

/** GET /merchants?page=&limit=&search=. Staff, admin and super_admin only — a merchant token gets 403, left to throw. */
export async function listMerchants(params: ListMerchantsParams = {}): Promise<MerchantsPage> {
    if (useFixtures) return devMock.listMerchants(params);
    try {
        const res = await axios.get(`${RECEPTIONIST_BASE}/merchants`, { headers: authHeaders(), params });
        const body = unwrap<MerchantsPage>(res);
        return { ...body, merchants: body.merchants ?? [] };
    } catch (err) {
        catchErrorLog(`[receptionist] listMerchants failed: ${err}`);
        throw err;
    }
}

// ─── Staff channel (#4711) ───────────────────────────────────────────────────

/**
 * GET .../merchants/{merchantId}/staff-channel — who we can actually reach.
 *
 * Not the same facts as `MerchantConfig.staffChannel`, which carries only the
 * name and the roll-up. This is the per-member truth plus `connectLink`, the
 * only thing that moves a merchant from "nobody connected" to a working staff
 * channel: there is no WhatsApp group to join, so the team connects itself by
 * messaging the merchant's number (ourteam-backend#4711).
 */
export async function getStaffChannel(merchantId: string): Promise<StaffChannelState> {
    if (useFixtures) return devMock.getStaffChannel(merchantId);
    try {
        const res = await axios.get(`${RECEPTIONIST_BASE}/merchants/${merchantId}/staff-channel`, { headers: authHeaders() });
        const body = unwrap<StaffChannelState>(res);
        return { ...body, members: body?.members ?? [] };
    } catch (err) {
        catchErrorLog(`[receptionist] getStaffChannel failed for ${merchantId}: ${err}`);
        throw err;
    }
}

/**
 * POST .../merchants/{merchantId}/staff-channel/test.
 *
 * 409 `staff_channel_not_connected` is an expected answer, not a failure: it
 * means nobody has messaged in yet. The server refuses to answer 200 there on
 * purpose — a success would tell the merchant their alerts work when nothing
 * was delivered — so this is a result union, not a throw.
 */
export async function sendStaffChannelTest(merchantId: string): Promise<SendStaffTestResult> {
    if (useFixtures) return devMock.sendStaffChannelTest(merchantId);
    try {
        const res = await axios.post(`${RECEPTIONIST_BASE}/merchants/${merchantId}/staff-channel/test`, {}, { headers: authHeaders() });
        const body = unwrap<{ delivered?: boolean; sentTo?: unknown[]; memberCount?: number }>(res);
        return {
            ok: true,
            delivered: body?.delivered ?? false,
            sentTo: body?.sentTo ?? [],
            memberCount: body?.memberCount ?? 0,
        };
    } catch (err) {
        const e = readApiError(err, "Could not send the test message");
        if (e.status === 409) return { ok: false, kind: "not_connected", message: e.message };
        catchErrorLog(`[receptionist] sendStaffChannelTest failed for ${merchantId}: ${err}`);
        return { ok: false, kind: "error", message: e.message };
    }
}

// ─── WhatsApp onboarding (#4671) ─────────────────────────────────────────────

/** GET .../merchants/{merchantId}/whatsapp — connection, checklist, templates. */
export async function getWhatsappState(merchantId: string): Promise<WhatsappState> {
    if (useFixtures) return devMock.getWhatsappState(merchantId);
    try {
        const res = await axios.get(`${RECEPTIONIST_BASE}/merchants/${merchantId}/whatsapp`, { headers: authHeaders() });
        const s = unwrap<WhatsappState>(res);
        return { ...s, templates: s.templates ?? [] };
    } catch (err) {
        catchErrorLog(`[receptionist] getWhatsappState failed for ${merchantId}: ${err}`);
        throw err;
    }
}

/** POST .../whatsapp/accept-terms — records the one-way door. Migration is refused without it. */
export async function acceptWhatsappTerms(merchantId: string): Promise<void> {
    if (useFixtures) return devMock.acceptWhatsappTerms(merchantId);
    try {
        await axios.post(`${RECEPTIONIST_BASE}/merchants/${merchantId}/whatsapp/accept-terms`, { accepted: true }, { headers: authHeaders() });
    } catch (err) {
        catchErrorLog(`[receptionist] acceptWhatsappTerms failed for ${merchantId}: ${err}`);
        throw err;
    }
}

/** POST .../whatsapp/migrate — 202, long-running; the screen polls getWhatsappState. */
export async function startWhatsappMigration(merchantId: string): Promise<MigrateResult> {
    if (useFixtures) return devMock.startWhatsappMigration(merchantId);
    try {
        const res = await axios.post(`${RECEPTIONIST_BASE}/merchants/${merchantId}/whatsapp/migrate`, {}, { headers: authHeaders() });
        return { ok: true, state: unwrap<WhatsappState>(res) };
    } catch (err) {
        const e = readApiError(err, "Could not start the move");
        if (e.status === 409 && e.code === "one_way_door_not_accepted") return { ok: false, kind: "terms_not_accepted", message: e.message };
        catchErrorLog(`[receptionist] startWhatsappMigration failed for ${merchantId}: ${err}`);
        return { ok: false, kind: "error", message: e.message };
    }
}

/** POST .../knowledge/sign-off — Services is right; ticks "Knowledge base signed off" (ourteam-backend#6674). */
export async function signOffKnowledgeBase(merchantId: string): Promise<WhatsappState> {
    if (useFixtures) return devMock.signOffKnowledgeBase(merchantId);
    try {
        const res = await axios.post(`${RECEPTIONIST_BASE}/merchants/${merchantId}/knowledge/sign-off`, {}, { headers: authHeaders() });
        const s = unwrap<WhatsappState>(res);
        return { ...s, templates: s.templates ?? [] };
    } catch (err) {
        catchErrorLog(`[receptionist] signOffKnowledgeBase failed for ${merchantId}: ${err}`);
        throw err;
    }
}

/** POST .../whatsapp/templates/submit — `{ keys }`, or `{}` for every unsubmitted one. */
export async function submitTemplates(merchantId: string, keys?: string[]): Promise<WhatsappState> {
    if (useFixtures) return devMock.submitTemplates(merchantId, keys);
    try {
        const res = await axios.post(`${RECEPTIONIST_BASE}/merchants/${merchantId}/whatsapp/templates/submit`, keys?.length ? { keys } : {}, { headers: authHeaders() });
        const s = unwrap<WhatsappState>(res);
        return { ...s, templates: s.templates ?? [] };
    } catch (err) {
        catchErrorLog(`[receptionist] submitTemplates failed for ${merchantId}: ${err}`);
        throw err;
    }
}

// ─── Learn from chats (#4672) ────────────────────────────────────────────────

/** POST .../transcripts (multipart, field `files`). A file that fails to parse is a `failed` row, not a failed request. */
export async function uploadTranscripts(merchantId: string, files: File[]): Promise<TranscriptFile[]> {
    if (useFixtures) return devMock.uploadTranscripts(merchantId, files);
    try {
        const form = new FormData();
        files.forEach((f) => form.append("files", f));
        // No Content-Type here: axios sets the multipart boundary itself.
        const headers = authHeaders();
        delete headers["Content-Type"];
        const res = await axios.post(`${RECEPTIONIST_BASE}/merchants/${merchantId}/transcripts`, form, { headers, timeout: 120000 });
        const body = unwrap<{ files?: TranscriptFile[] }>(res);
        return Array.isArray(body?.files) ? body.files : [];
    } catch (err) {
        catchErrorLog(`[receptionist] uploadTranscripts failed for ${merchantId}: ${err}`);
        throw err;
    }
}

/** POST .../transcripts/analyse — sync within ~30s, else 202 with a job to poll. */
export async function analyseTranscripts(merchantId: string, fileIds: string[]): Promise<AnalyseResult> {
    if (useFixtures) return devMock.analyseTranscripts(merchantId, fileIds);
    try {
        const res = await axios.post(`${RECEPTIONIST_BASE}/merchants/${merchantId}/transcripts/analyse`, { fileIds }, { headers: authHeaders(), timeout: 60000 });
        const body = unwrap<AnalyseResult>(res);
        return body.status === "done" ? { ...body, proposals: body.proposals ?? [] } : body;
    } catch (err) {
        catchErrorLog(`[receptionist] analyseTranscripts failed for ${merchantId}: ${err}`);
        throw err;
    }
}

/** GET .../transcripts/analyse/{jobId} — the poll for a 202. */
export async function getAnalysisJob(merchantId: string, jobId: string): Promise<AnalyseResult> {
    if (useFixtures) return devMock.getAnalysisJob(merchantId, jobId);
    try {
        const res = await axios.get(`${RECEPTIONIST_BASE}/merchants/${merchantId}/transcripts/analyse/${jobId}`, { headers: authHeaders() });
        const body = unwrap<AnalyseResult>(res);
        return body.status === "done" ? { ...body, proposals: body.proposals ?? [] } : body;
    } catch (err) {
        catchErrorLog(`[receptionist] getAnalysisJob failed for ${merchantId}/${jobId}: ${err}`);
        throw err;
    }
}

/** POST .../transcripts/apply — writes only the given ids; 422 writes nothing. */
export async function applyProposals(merchantId: string, proposalIds: string[]): Promise<ApplyProposalsResult> {
    if (useFixtures) return devMock.applyProposals(merchantId, proposalIds);
    try {
        const res = await axios.post(`${RECEPTIONIST_BASE}/merchants/${merchantId}/transcripts/apply`, { proposalIds }, { headers: authHeaders() });
        const body = unwrap<{ merchant: MerchantConfig; flow: BookingFlow }>(res);
        return { ok: true, merchant: body.merchant, flow: body.flow };
    } catch (err) {
        const e = readApiError(err, "Could not apply these changes");
        if (e.status === 422) {
            const listed = e.data?.problems;
            const problems = Array.isArray(listed) ? (listed as string[]) : [e.message];
            return { ok: false, kind: "invalid", problems };
        }
        catchErrorLog(`[receptionist] applyProposals failed for ${merchantId}: ${err}`);
        return { ok: false, kind: "error", message: e.message };
    }
}

/** DELETE .../transcripts — every uploaded thread, now. The 30-day job is the backstop. */
export async function deleteTranscripts(merchantId: string): Promise<void> {
    if (useFixtures) return devMock.deleteTranscripts(merchantId);
    try {
        await axios.delete(`${RECEPTIONIST_BASE}/merchants/${merchantId}/transcripts`, { headers: authHeaders() });
    } catch (err) {
        catchErrorLog(`[receptionist] deleteTranscripts failed for ${merchantId}: ${err}`);
        throw err;
    }
}
